import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * DropdownMenu Component
 *
 * Reusable dropdown menu with trigger button and menu items.
 * Supports custom trigger rendering and menu item configuration.
 *
 * Props:
 *   trigger - React node or function that receives isOpen state
 *   items - Array of menu item objects with:
 *     - label: Display text
 *     - onClick: Click handler
 *     - checked: Optional boolean for checkbox indicator
 *     - icon: Optional icon component
 *   className - Additional classes for the dropdown container
 */
function DropdownMenu({
  trigger,
  items = [],
  className = '',
  menuClassName = '',
  placement = 'bottom',
  autoFlip = false,
  usePortal = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [effectivePlacement, setEffectivePlacement] = useState(placement);
  const [menuStyle, setMenuStyle] = useState({ top: 0, left: 0, visibility: 'hidden' });
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (!dropdownRef.current) return;

      const clickedTrigger = dropdownRef.current.contains(event.target);
      const clickedMenu = menuRef.current?.contains(event.target);

      if (!clickedTrigger && !clickedMenu) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    setEffectivePlacement(placement);
  }, [placement, isOpen]);

  useLayoutEffect(() => {
    if (!isOpen || !autoFlip || !menuRef.current || !triggerRef.current) {
      return;
    }

    const margin = 8;

    const applyPlacement = () => {
      if (!menuRef.current || !triggerRef.current) return;

      const menuRect = menuRef.current.getBoundingClientRect();
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const spaceAbove = triggerRect.top;
      const spaceBelow = window.innerHeight - triggerRect.bottom;

      if (placement === 'top') {
        setEffectivePlacement(spaceAbove >= menuRect.height + margin ? 'top' : 'bottom');
      } else {
        setEffectivePlacement(spaceBelow >= menuRect.height + margin ? 'bottom' : 'top');
      }
    };

    applyPlacement();
    window.addEventListener('resize', applyPlacement);

    return () => {
      window.removeEventListener('resize', applyPlacement);
    };
  }, [isOpen, autoFlip, placement, items.length]);

  useLayoutEffect(() => {
    if (!isOpen || !usePortal || !menuRef.current || !triggerRef.current) {
      return;
    }

    const margin = 8;

    const positionMenu = () => {
      if (!menuRef.current || !triggerRef.current) return;

      const menuRect = menuRef.current.getBoundingClientRect();
      const triggerRect = triggerRef.current.getBoundingClientRect();

      const preferredPlacement = autoFlip
        ? (() => {
            const spaceAbove = triggerRect.top;
            const spaceBelow = window.innerHeight - triggerRect.bottom;
            if (placement === 'top') {
              return spaceAbove >= menuRect.height + margin ? 'top' : 'bottom';
            }
            return spaceBelow >= menuRect.height + margin ? 'bottom' : 'top';
          })()
        : placement;

      if (preferredPlacement !== effectivePlacement) {
        setEffectivePlacement(preferredPlacement);
      }

      const top = preferredPlacement === 'top'
        ? triggerRect.top - menuRect.height - margin
        : triggerRect.bottom + margin;

      const desiredLeft = triggerRect.right - menuRect.width;
      const maxLeft = window.innerWidth - menuRect.width - margin;
      const boundedLeft = Math.max(margin, Math.min(desiredLeft, maxLeft));

      setMenuStyle({
        top: Math.max(margin, top),
        left: boundedLeft,
        visibility: 'visible',
      });
    };

    positionMenu();
    window.addEventListener('resize', positionMenu);
    window.addEventListener('scroll', positionMenu, true);

    return () => {
      window.removeEventListener('resize', positionMenu);
      window.removeEventListener('scroll', positionMenu, true);
    };
  }, [isOpen, usePortal, autoFlip, placement, effectivePlacement, items.length]);

  const toggleDropdown = () => setIsOpen(!isOpen);

  const placementClass = effectivePlacement === 'top' ? 'bottom-full mb-2' : 'top-full mt-2';

  const menuNode = isOpen ? (
    <div
      ref={menuRef}
      className={`w-56 rounded-lg border border-gray-700 bg-[var(--axial-card)] shadow-lg z-50 ${
        usePortal ? 'fixed' : `absolute right-0 ${placementClass}`
      } ${menuClassName}`}
      style={usePortal ? menuStyle : undefined}
    >
      <div className="py-1">
        {items.map((item, index) => (
          <button
            key={index}
            type="button"
            disabled={Boolean(item.disabled)}
            onClick={() => {
              if (item.disabled) {
                return;
              }
              item.onClick?.();
              setIsOpen(false);
            }}
            className={`w-full px-4 py-2 text-left text-xs flex items-center justify-between transition-colors ${
              item.disabled
                ? 'cursor-not-allowed text-gray-500'
                : 'text-gray-200 hover:bg-gray-700/50'
            }`}
          >
            <span className="flex items-center gap-2">
              {item.icon && <span className="flex-shrink-0">{item.icon}</span>}
              {item.label}
            </span>
            {item.checked !== undefined && (
              <div className="flex-shrink-0">
                {item.checked ? (
                  <svg className="w-4 h-4 text-blue-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                  </svg>
                ) : (
                  <div className="w-4 h-4 border border-gray-600 rounded" />
                )}
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  ) : null;

  return (
    <div className={`relative inline-block ${className}`} ref={dropdownRef}>
      <div ref={triggerRef} onClick={toggleDropdown}>
        {typeof trigger === 'function' ? trigger(isOpen) : trigger}
      </div>

      {usePortal ? (menuNode ? createPortal(menuNode, document.body) : null) : menuNode}
    </div>
  );
}

export default DropdownMenu;
